// Country panel (left drawer, opens on selection): flag-colour header, leaders with since
// dates, relation to you (reason + sources), key stats with data years (grey when older than
// 3 years), memberships, wars and statements. Every number carries a source chip and a claim
// label; less central indicators fold away under "More statistics".

import { useSignal } from '@preact/signals';
import type { Statement, War } from '../core/schema.ts';
import type { CountryReport } from '../world/api.ts';
import { rgba, shade } from './color.ts';
import { useServices } from './context.ts';
import { fmtDate, fmtStat, isStale } from './format.ts';
import { Icon } from './icons.tsx';
import { statementBase, uniqueKeys } from './keys.ts';
import { emblemOf, reportOf, selfIso } from './nation.ts';
import { ClaimLabel, Corners, Medallion, RelationTag, SourceChip } from './parts.tsx';
import { QuoteText } from './QuoteText.tsx';

const STATEMENTS_SHOWN = 3;

// The key statistics, in display order. Indicator ids differ by bake version; the first
// id present wins.
const KEY_STATS: Array<{ name: string; ids: string[] }> = [
  { name: 'GDP', ids: ['gdp', 'gdp_usd', 'gdp_nominal'] },
  { name: 'Population', ids: ['population', 'pop'] },
  { name: 'Hard power', ids: ['hard_power', 'milex_share'] },
  { name: 'GDP per capita', ids: ['gdp_per_capita', 'gdp_pc_usd'] },
  { name: 'Exports', ids: ['exports', 'exports_usd'] },
  { name: 'R&D (% of GDP)', ids: ['rd_share', 'rd_gdp_pct'] },
];

// Display names for the other indicator ids; anything unlisted shows its id.
const STAT_NAMES: Record<string, string> = {
  gdp_ppp: 'GDP (PPP)',
  gdp_ppp_usd: 'GDP (PPP)',
  milex_usd: 'Military spending',
  warheads: 'Nuclear warheads (estimate)',
  tourist_arrivals: 'Tourist arrivals',
  tourism: 'Tourist arrivals',
  co2_pc_t: 'CO₂ per person',
  democracy: 'Electoral democracy (V-Dem)',
  hdi: 'Human Development Index',
  life_exp: 'Life expectancy',
  urban_pct: 'Urban population share',
  unga_ideal: 'UNGA ideal point',
  embassies: 'Diplomatic posts',
  ai_models: 'Notable AI models',
  top500_rmax: 'TOP500 Rmax',
  papers: 'Research articles',
  olympic_medals: 'Olympic medals',
  olympic_gold: 'Olympic golds',
  oil_prod: 'Oil production',
  gas_prod: 'Gas production',
  coal_prod: 'Coal production',
};

type Stat = CountryReport['stats'][string];

/** Key stats present in the report, then every other stat id. */
function splitStats(stats: CountryReport['stats']): { key: Array<{ name: string; stat: Stat }>; other: string[] } {
  const used = new Set<string>();
  const key: Array<{ name: string; stat: Stat }> = [];
  for (const k of KEY_STATS) {
    const id = k.ids.find((x) => stats[x] !== undefined);
    if (!id) {
      continue;
    }
    k.ids.forEach((x) => used.add(x));
    key.push({ name: k.name, stat: stats[id] });
  }
  const other = Object.keys(stats).filter((id) => !used.has(id));
  return { key, other };
}

const ROLE_NAMES: Record<string, string> = {
  head_of_state: 'Head of state',
  head_of_government: 'Head of government',
  paramount_leader: 'Paramount leader',
};

export function CountryPanel() {
  const services = useServices();
  const { state, world } = services;
  const sel = state.selection.value;
  if (!sel || !sel.iso3) {
    return null;
  }
  const report = reportOf(world, sel.iso3);
  if (!report) {
    return null;
  }
  const self = selfIso(services);
  return <CountryBody key={sel.iso3} report={report} isSelf={report.country.iso3 === self} />;
}

function CountryBody({ report, isSelf }: { report: CountryReport; isSelf: boolean }) {
  const { state, world, actions } = useServices();
  const c = report.country;
  const date = state.date.value;
  const stats = splitStats(report.stats);
  const more = useSignal(false);
  const shown = report.statements.slice(0, STATEMENTS_SHOWN);
  const shownKeys = uniqueKeys(shown.map(statementBase));

  const headerBg = `linear-gradient(115deg, ${rgba(c.colors.primary, 0.95)} 0%, ${rgba(shade(c.colors.primary, -0.45), 0.95)} 70%), ${c.colors.primary}`;

  return (
    <aside class="wc-cdrawer wc-panel" aria-label={`${c.name} details`}>
      <Corners />
      <header class="wc-country-head" style={{ background: headerBg }}>
        <span class="wc-country-stripe" style={{ background: c.colors.secondary }} />
        <Medallion {...emblemOf(world, c.iso3, 'state')} size={64} relation={isSelf ? undefined : report.relation.relation} />
        <div class="wc-country-titles">
          <h2 class="wc-country-name">{c.name}</h2>
          <div class="wc-country-sub">
            {c.iso3}
            {c.un_member ? ' · UN member' : ''}
            {c.capital ? ` · capital ${c.capital.name}` : ''}
            <SourceChip ids={c.sources} />
          </div>
        </div>
        <button type="button" class="wc-ibtn wc-country-x" aria-label="Close" onClick={() => actions.select(null)}>
          <Icon name="close" size={14} />
        </button>
      </header>

      <div class="wc-country-body wc-scroll">
        {isSelf ? null : (
          <section>
            <h3 class="wc-rule-title">Relation to you</h3>
            <div class="wc-country-rel">
              <RelationTag relation={report.relation.relation} />
              <SourceChip ids={report.relation.sources} />
            </div>
            <p class="wc-country-reason">{report.relation.reason}</p>
          </section>
        )}

        <section>
          <h3 class="wc-rule-title">Leaders</h3>
          {report.leaders.length === 0 ? <div class="wc-empty">No sourced leaders at this date</div> : null}
          {report.leaders.map((l) => (
            <div class="wc-row" key={l.role + l.name}>
              <span class="wc-row-key">{ROLE_NAMES[l.role] ?? l.role}</span>
              <span class="wc-row-val">
                {l.name}
                <span class="wc-year"> since {fmtDate(l.since)}</span>
              </span>
              <SourceChip ids={l.sources} />
            </div>
          ))}
        </section>

        <section>
          <h3 class="wc-rule-title">Key statistics</h3>
          {stats.key.length === 0 ? <div class="wc-empty">No sourced statistics</div> : null}
          <div class="wc-stats">
            {stats.key.map(({ name, stat }) => (
              <StatCard key={name} name={name} stat={stat} date={date} />
            ))}
          </div>
          {stats.other.length > 0 ? (
            <>
              <button type="button" class="wc-more" aria-expanded={more.value} onClick={() => (more.value = !more.value)}>
                <Icon name={more.value ? 'chevronUp' : 'chevronDown'} size={13} />
                {more.value ? 'Fewer statistics' : `More statistics (${stats.other.length})`}
              </button>
              {more.value ? (
                <div class="wc-stat-rows">
                  {stats.other.map((id) => (
                    <StatRow key={id} name={STAT_NAMES[id] ?? id.replace(/_/g, ' ')} stat={report.stats[id]} date={date} />
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
          <div class="wc-country-note wc-year">Grey = data older than 3 years; the chip shows the data year.</div>
        </section>

        <section>
          <h3 class="wc-rule-title">Memberships</h3>
          {report.memberships.length === 0 ? <div class="wc-empty">None at this date</div> : null}
          <div class="wc-members">
            {report.memberships.map((m) => (
              <span class="wc-member" key={m.bloc}>
                <b>{m.bloc}</b>
                {m.status !== 'member' ? <i> · {m.status}</i> : null}
                <span class="wc-year"> {m.since.slice(0, 4)}</span>
                <SourceChip ids={m.sources} />
              </span>
            ))}
          </div>
        </section>

        <section>
          <h3 class="wc-rule-title">Wars</h3>
          {report.wars.length === 0 ? <div class="wc-empty">No active wars at this date</div> : null}
          {report.wars.map((w) => (
            <WarRow key={w.id} war={w} />
          ))}
        </section>

        {report.statements.length > 0 ? (
          <section>
            <h3 class="wc-rule-title">Statements on wars</h3>
            {shown.map((s, i) => (
              <StatementCard key={shownKeys[i]} s={s} />
            ))}
          </section>
        ) : null}
      </div>

      <footer class="wc-country-foot">
        {c.capital ? (
          <button type="button" class="wc-btn" onClick={() => c.capital && actions.flyTo(c.capital.hex)}>
            <Icon name="target" size={14} />
            Capital
          </button>
        ) : null}
        {isSelf ? null : (
          <button type="button" class="wc-btn wc-btn--gold" onClick={() => actions.openPanel('diplomacy', c.iso3)}>
            <Icon name="scales" size={14} />
            Diplomacy
          </button>
        )}
      </footer>
    </aside>
  );
}

function StatCard({ name, stat, date }: { name: string; stat: Stat; date: string }) {
  const stale = isStale(stat.year, date);
  return (
    <div class={stale ? 'wc-stat wc-stat--stale' : 'wc-stat'} title={stat.unit}>
      <span class="wc-stat-name">{name}</span>
      <span class="wc-stat-val wc-num">{fmtStat(stat.value, stat.unit)}</span>
      <span class="wc-stat-foot">
        <ClaimLabel kind="data" />
        <SourceChip ids={stat.sources} year={stat.year} />
      </span>
    </div>
  );
}

function StatRow({ name, stat, date }: { name: string; stat: Stat; date: string }) {
  const stale = isStale(stat.year, date);
  return (
    <div class={stale ? 'wc-row wc-stat--stale' : 'wc-row'} title={stat.unit}>
      <span class="wc-row-key">{name}</span>
      <span class="wc-row-val wc-num wc-stat-val">{fmtStat(stat.value, stat.unit)}</span>
      <SourceChip ids={stat.sources} year={stat.year} />
    </div>
  );
}

function WarRow({ war }: { war: War }) {
  const { actions } = useServices();
  return (
    <button type="button" class="wc-war-row" onClick={() => actions.openPanel('war', war.id)}>
      <Icon name="war" size={16} />
      <span class="wc-war-row-name">{war.name}</span>
      <span class={`wc-war-status wc-war-status--${war.status.value}`}>{war.status.value}</span>
      <Icon name="chevronRight" size={14} />
    </button>
  );
}

export function StatementCard({ s }: { s: Statement }) {
  return (
    <figure class="wc-statement wc-parchment">
      <blockquote class="wc-quote">
        <QuoteText quote={s.quote} />
      </blockquote>
      <figcaption>
        <span>
          — {s.speaker}, {s.venue}, {fmtDate(s.date)}
        </span>
        <span class="wc-statement-tags">
          <ClaimLabel kind="official" />
          <SourceChip ids={s.sources} />
        </span>
      </figcaption>
    </figure>
  );
}
